import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import {SearchBox} from '../SearchBox';
import SearchForm from '../SearchForm';
import SearchCta from '../SearchCta';

import {constructSearchRequest, generateSearchPageLink} from '../../utils/search';
import {countOfNights} from '../../utils/dates';
import {getSearchValues, removeDatesFromTripCookie, removeDatesFromSearchCookie} from '../../utils/cookies';

import './style.less';

export const SMART_SHOW_NEXT_TOOLTIP = 'smart';
export const NORMAL_SHOW_NEXT_TOOLTIP = 'normal';

export function isSearchDataDifferent(nextProps, props) {
    return (
        nextProps.checkIn !== props.checkIn ||
        nextProps.checkOut !== props.checkOut ||
        nextProps.guests !== props.guests
    );
}

export default class Search extends Component {
    static propTypes = {
        /** Destination label */
        destination: PropTypes.string,
        /** Location Based Search ID */
        lbsId: PropTypes.string.isRequired,
        /** Selected check-in date */
        checkIn: PropTypes.string,
        /** Selected check-out date */
        checkOut: PropTypes.string,
        /** Guests information */
        guests: PropTypes.shape({
            adultsCount: PropTypes.number,
            childrenCount: PropTypes.number,
            petIncluded: PropTypes.bool
        }),
        /** Max Number of Guests */
        maxNumberOfGuests: PropTypes.number,
        /** Get the search sticked to the top on scroll */
        stickScrollTop: PropTypes.number,
        /** Function to call when search form is submitted */
        onSubmit: PropTypes.func.isRequired,
        /** Function to call when component updates */
        onUpdate: PropTypes.func,
        /** Labels to display */
        labels: PropTypes.shape({
            mobileTitle: PropTypes.string,
            buttonTitle: PropTypes.string,
            datePicker: PropTypes.shape({}),
            guests: PropTypes.shape({})
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** Activates mobile mode for searchbox */
        isStickyMobile: PropTypes.bool,
        /** Additional props for Typeahead */
        typeaheadProps: PropTypes.shape({}),
        /** Edap event name */
        edapEventName: PropTypes.shape({}),
        /** Edap refinements */
        edapRefinements: PropTypes.arrayOf(PropTypes.string),
        /** Edap action location */
        edapActionlocation: PropTypes.string,
        /** Domain */
        domain: PropTypes.string,
        /** Property Type */
        propertyType: PropTypes.string,
        /** Opens guest tooltip after date range is selected */
        showNextTooltip: PropTypes.bool,
        /** showNextTooltip behaviour */
        showNextTooltipMode: PropTypes.oneOf([NORMAL_SHOW_NEXT_TOOLTIP, SMART_SHOW_NEXT_TOOLTIP]),
        /** By default the date-range-picker has separate fields for check-in and check-out. To consolidate these fields into a single field, set this prop to 'true' */
        consolidateInputs: PropTypes.bool,
        /** Enables date picker improvements ('consolidatedSearchBox' and 'consolidateInputs' props will be enabled) */
        smartDatePicker: PropTypes.bool,
        /** Controls if the typeahead-icon is displayed or not */
        typeaheadIcon: PropTypes.bool,
        /** Date-picker icon to display */
        datePickerIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func, PropTypes.bool]),
        /** Guest-picker icon to display */
        guestPickerIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func, PropTypes.bool]),
        /** expanded searchbox on mobile */
        expandedSearchBox: PropTypes.bool,
        /** prevent search from guestpicker*/
        submitFromGuestPicker: PropTypes.bool,
        /** consolidatedSearchBox ux */
        consolidatedSearchBox: PropTypes.bool,
        /** Hide datepicker clear button when no date is selected */
        hideClearDatesBtn: PropTypes.bool,
        /** Apply styles to mobile calendar emulating stab look and fool  */
        stabTheme: PropTypes.bool,
        /** Brand  */
        brand: PropTypes.string,
        /** Show the three stabSearchbox icons instead of the react ones  */
        showStabIcons: PropTypes.bool,
        /** Labels for datepicker calendar */
        calendarLabels: PropTypes.shape({}),
        /** Shows datepicker animated cue */
        animatedCue: PropTypes.bool,
        displayYearFirst: PropTypes.bool
    };

    static defaultProps = {
        destination: null,
        lbsId: null,
        guests: {
            adultsCount: null,
            childrenCount: null,
            petIncluded: null
        },
        maxNumberOfGuests: 10,
        onUpdate: () => {},
        locale: 'en_US',
        labels: {
            mobileTitle: 'Search',
            buttonTitle: 'Search',
            datePicker: {
                checkIn: 'Check In',
                checkOut: 'Check Out',
                labels: 'Clear',
                done: 'Done',
                arrival: 'Select your arrival date',
                departure: 'Select your departure date'
            },
            guests: {
                label: 'Guests',
                adult: 'Adult',
                adults: 'Adults',
                child: 'Child',
                children: 'Children',
                pets: 'pets',
                guests: 'guests',
                yesBtn: 'Yes',
                noBtn: 'No',
                clearBtn: 'Clear',
                applyBtn: 'Apply',
                modalTitle: 'Select guest ',
                mobileBtn: 'Continue'
            },
            typeahead: {
                destinationplaceholdermsg: 'Where to?'
            }
        },
        isStickyMobile: false,
        edapEventName: {
            name: 'search.submit',
            datedName: 'dated.search.submit'
        },
        edapRefinements: [],
        edapActionlocation: 'search-widget',
        domain: 'wwww.homeaway.com',
        consolidateInputs: false,
        smartDatePicker: false,
        typeaheadIcon: true,
        datePickerIcon: false,
        guestPickerIcon: false,
        expandedSearchBox: false,
        submitFromGuestPicker: true,
        consolidatedSearchBox: false,
        hideClearDatesBtn: false,
        stabTheme: false,
        brand: '',
        showNextTooltip: false,
        showNextTooltipMode: NORMAL_SHOW_NEXT_TOOLTIP,
        showStabIcons: false,
        animatedCue: false,
        displayYearFirst: false
    };

    state = {
        guests: this.props.guests,
        lbsId: this.props.lbsId,
        isLoadingSerp: false,
        isTypeaheadInputFocus: false,
        destination: this.props.destination,
        checkIn: this.props.checkIn,
        checkOut: this.props.checkOut,
        isDatePickerOpened: false,
        isGuestPickerOpened: false
    }

    componentDidMount() {
        window.addEventListener('pageshow', this.handlePersistedState, false);

        const searchCookies = getSearchValues();

        this.setState({ // eslint-disable-line react/no-did-mount-set-state
            checkIn: this.props.checkIn ? this.props.checkIn : searchCookies.arrivalDate,
            checkOut: this.props.checkOut ? this.props.checkOut : searchCookies.departureDate,
            guests: this.getGuests(searchCookies),
            destination: this.props.destination ? this.props.destination : searchCookies.destination
        });
    }

    componentWillReceiveProps(nextProps) {
        const isNewSearchData = nextProps.checkIn || nextProps.checkOut;
        if (isNewSearchData && isSearchDataDifferent(nextProps, this.props)) {
            this.setState({
                checkIn: nextProps.checkIn,
                checkOut: nextProps.checkOut,
                guests: nextProps.guests
            });
        }
    }

    componentWillUpdate(nextProps, nextState) {
        if (this.props.onUpdate) {
            this.props.onUpdate(nextState);
        }
    }

    getGuests(guests) {
        return {
            adultsCount: this.props.guests.adultsCount !== null ? this.props.guests.adultsCount : guests.adults,
            childrenCount: this.props.guests.childrenCount !== null ? this.props.guests.childrenCount : guests.children,
            petIncluded: this.props.guests.petIncluded !== null ? this.props.guests.petIncluded : guests.petIncluded
        };
    }

    handlePersistedState = () => {
        this.setState({isLoadingSerp: false});
    }

    canOpenNextTooltip() {
        let canOpenNextTooltip = !this.isMobile() && this.props.showNextTooltip;
        for (let i = 0; i < arguments.length; ++i) {
            canOpenNextTooltip = Boolean(canOpenNextTooltip && arguments[i]);
        }
        return canOpenNextTooltip;
    }

    onSearchSelected = (query) => {
        this.setState({
            destination: query.destination,
            subDestination: query.subDestination || {},
            lbsId: query.destinationUuid
        });

        const {checkIn, checkOut, guests: {adultsCount}} = this.state;
        const {showNextTooltipMode} = this.props;

        if (!this.canOpenNextTooltip()) {
            return;
        }

        const openCalendar = !checkIn && !checkOut;
        const openGuestPicker = !adultsCount;
        let inputToOpen;

        if (showNextTooltipMode === NORMAL_SHOW_NEXT_TOOLTIP || openCalendar) {
            inputToOpen = 'isDatePickerOpened';
        } else if (openGuestPicker) {
            inputToOpen = 'isGuestPickerOpened';
        } else {
            return;
        }

        this.setState({
            [inputToOpen]: true
        });
    }

    onDatePickerClose = () => {
        const obj = {
            isDatePickerOpened: false
        };

        const checkIn = Boolean(this.state.checkIn);
        const checkOut = Boolean(this.state.checkOut);
        if (!checkIn || !checkOut) {
            obj.checkIn = '';
            obj.checkOut = '';
        }
        if (checkIn && checkOut && this.isMobile()) {
            obj.isGuestPickerOpened = true;
        }
        this.setState(obj);
    }

    onInputCheckIn = (value) => {
        this.setState({checkIn: value});
    };

    onInputCheckOut = (value) => {
        this.setState((prevState) => ({
            checkOut: value,
            isDatePickerOpened: this.isMobile() ? true : !(prevState.checkIn && value),
            isGuestPickerOpened: this.canOpenNextTooltip(prevState.checkIn, value, this.props.showNextTooltipMode === NORMAL_SHOW_NEXT_TOOLTIP || !this.state.guests.adultsCount)
        }));
    };

    onInputGuest = (key, value) => {
        if (key === 'clear') {
            this.setState({guests: value});
        } else {
            this.setState({guests: {...this.state.guests, [key]: value}});
        }
    };

    onInputGuestClose = () => {
        this.setState({isGuestPickerOpened: false, isDatePickerOpened: false}, () => {
            const isMobile = this.isMobile();
            const areAllInputsFilled = this.state.destination && this.state.checkIn && this.state.checkOut;

            if (this.props.submitFromGuestPicker && isMobile && areAllInputsFilled) {
                this.onSubmit();
            }
        });
    };

    onInputGuestExit = (undoState) => {
        this.setState({guests: undoState});
    }

    isMobile() {
        return this.props.isStickyMobile || this.state.isModalOpen;
    }

    onSubmitButtonClicked = (e) => {
        e.preventDefault();
        this.onSubmit();
    }

    getRefinements(event) {
        const {guests, checkIn, checkOut} = event;
        const adults = guests.adultsCount || 0;
        const children = guests.childrenCount || 0;
        const pets = guests.petIncluded || false;
        const edapRefinements = [
            `adults_${adults}`,
            `children_${children}`,
            `sleeps_${adults + children}`,
            `pets_${pets}`
        ];

        if (checkIn && checkOut) {
            edapRefinements.push(`nights_${countOfNights(checkIn, checkOut)}`);
            const currenDate = new Date();
            edapRefinements.push(`window_${countOfNights(currenDate, checkIn)}`);
        }

        return edapRefinements.join(',');
    }

    getEdapData(event) {
        return {
            actionlocation: this.props.edapActionlocation,
            eventrefinements: this.getRefinements(event),
            numadults: event.guests.adultsCount,
            withpets: event.guests.petIncluded,
            numchildren: event.guests.childrenCount
        };
    }

    pushEdapEvent(event) {
        const {checkIn, checkOut} = event;

        const adls = this.getEdapData(event);
        const hasDates = checkIn && checkOut;
        const edapEventName = hasDates ? this.props.edapEventName.datedName : this.props.edapEventName.name;

        if (hasDates) {
            adls.startdate = checkIn;
            adls.enddate = checkOut;
            adls.lengthofstay = countOfNights(checkIn, checkOut);
        }

        window.edap.push((edap) => edap.trigger(edapEventName, adls, {delayProcessing: true}));
    }

    onTypeaheadBlur = (event) => {
        if (event) {
            const destination = event.target ? event.target.value : null;
            this.setState({
                destination,
                isTypeaheadInputFocus: false,
                isGuestPickerOpened: false
            });
        }
    }

    onSubmit = () => {
        if (!this.state.destination) {
            this.setState({isTypeaheadInputFocus: true});
            return;
        }

        if (!this.state.checkIn && !this.state.checkOut) {
            removeDatesFromTripCookie();
            removeDatesFromSearchCookie();
        }

        this.setState({isLoadingSerp: true}, () => {
            window.edap = window.edap || [];

            const event = {
                checkIn: this.state.checkIn,
                checkOut: this.state.checkOut,
                guests: this.state.guests,
                destination: this.state.destination,
                subDestination: this.state.subDestination,
                lbsId: this.state.lbsId
            };

            this.pushEdapEvent(event);

            const searchRequest = constructSearchRequest(event);
            const searchPath = generateSearchPageLink(
                searchRequest,
                {propertyType: this.props.propertyType}
            );

            this.props.onSubmit({...event, searchPath});
        });
    }

    openModal = () => {
        this.setState({
            isModalOpen: true
        });
    }

    closeModal = () => {
        this.setState({
            isModalOpen: false
        });
    }

    render() {
        const {
            destination, guests, checkIn, checkOut, isModalOpen,
            isLoadingSerp, isGuestPickerOpened, isDatePickerOpened,
            isTypeaheadInputFocus
        } = this.state;

        const {
            labels, locale, typeaheadProps, consolidateInputs, typeaheadIcon, datePickerIcon, guestPickerIcon,
            expandedSearchBox, consolidatedSearchBox, smartDatePicker, hideClearDatesBtn, stabTheme,
            brand, maxNumberOfGuests, showStabIcons, calendarLabels, animatedCue, displayYearFirst, className
        } = this.props;

        const isMobile = this.isMobile();
        const applyStabTheme = !!(isMobile && stabTheme);
        const baseClassName = 'search__container';
        const classNames = cnames(baseClassName, {
            [`${baseClassName}--${brand}`]: applyStabTheme && !!brand,
            [`${baseClassName}--branded`]: applyStabTheme && !!brand,
            [`${className}`]: !!className
        });

        return (
            <div className={classNames}>
                <SearchCta label={destination || labels.typeahead.destinationplaceholdermsg} onClick={this.openModal} expandedSearchBox={expandedSearchBox} />
                <SearchBox
                    opened={isModalOpen}
                    onClose={this.closeModal}
                    title={labels.mobileTitle}
                    consolidatedSearchBox={smartDatePicker || consolidatedSearchBox}
                    smartDatePicker={!applyStabTheme && smartDatePicker}
                    {...{expandedSearchBox}}
                >
                    <SearchForm
                        query={destination}
                        maxNumGuests={maxNumberOfGuests}
                        onSubmit={this.onSubmitButtonClicked}
                        onBlur={this.onTypeaheadBlur}
                        onInputGuest={this.onInputGuest}
                        onInputGuestClose={this.onInputGuestClose}
                        onInputGuestExit={this.onInputGuestExit}
                        onInputCheckIn={this.onInputCheckIn}
                        onUpdate
                        onInputCheckOut={this.onInputCheckOut}
                        onClose={this.onDatePickerClose}
                        onSearchSelected={this.onSearchSelected}
                        consolidatedSearchBox={smartDatePicker || consolidatedSearchBox}
                        consolidateInputs={!applyStabTheme && (smartDatePicker || consolidateInputs)}
                        smartDatePicker={!applyStabTheme && smartDatePicker}
                        stabTheme={applyStabTheme}
                        {...{labels, locale, guests, checkIn, checkOut, typeaheadProps, isLoadingSerp,
                            isGuestPickerOpened, isDatePickerOpened, isTypeaheadInputFocus,
                            consolidatedSearchBox, hideClearDatesBtn, isMobile, calendarLabels, animatedCue,
                            showStabIcons, typeaheadIcon, datePickerIcon, guestPickerIcon, displayYearFirst}}
                    />
                </SearchBox>
            </div>
        );
    }
}
